import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../lib/api';
import Header from '../components/Header';
import { Footer } from '../components/MoreSections';
import OwnerListingCard from '../components/OwnerListingCard';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Home, ChevronRight, User, X } from 'lucide-react';

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const OwnerListingsPage = () => {
  const q = useQuery();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [intent, setIntent] = useState(q.get('intent') || 'all');
  const [city, setCity] = useState(q.get('city') || 'all');
  const [category, setCategory] = useState(q.get('category') || 'all');

  useEffect(() => {
    setLoading(true);
    const params = { limit: 60 };
    if (intent !== 'all') params.intent = intent;
    if (city !== 'all') params.city = city;
    if (category !== 'all') params.listing_category = category;
    axios.get(`${API}/owner-listings`, { params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [intent, city, category]);

  const cities = ['all', 'Gurgaon', 'Delhi', 'Noida', 'Bangalore', 'Mumbai', 'Pune', 'Hyderabad', 'Goa', 'Chennai', 'Kolkata'];
  const hasFilters = intent !== 'all' || city !== 'all' || category !== 'all';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
            <Link to="/" className="hover:text-[#9a1b2f] flex items-center gap-1"><Home className="w-3 h-3" /> Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-700 font-medium">Owner Listings</span>
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-gray-900 flex items-center gap-2"><User className="w-5 h-5 text-[#9a1b2f]" /> Listed by Owners</h1>
            <p className="text-xs text-gray-500 mt-1">{loading ? 'Loading...' : `${total} owner-listed ${total === 1 ? 'property' : 'properties'} available`}</p>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 grid lg:grid-cols-[280px_1fr] gap-6">
        <aside className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900">Filters</h3>
              {hasFilters && (
                <button onClick={() => { setIntent('all'); setCity('all'); setCategory('all'); }} className="text-xs text-[#9a1b2f] hover:underline flex items-center gap-1">
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold uppercase text-gray-500 tracking-wide">Intent</label>
                <Select value={intent} onValueChange={setIntent}>
                  <SelectTrigger className="h-9 mt-1 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="sell">For Sale</SelectItem>
                    <SelectItem value="rent">For Rent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase text-gray-500 tracking-wide">Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="h-9 mt-1 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase text-gray-500 tracking-wide">City</label>
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger className="h-9 mt-1 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {cities.map((c) => <SelectItem key={c} value={c}>{c === 'all' ? 'All Cities' : c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#9a1b2f] to-[#7a1524] text-white rounded-2xl p-5">
            <h4 className="font-bold">Are you a property owner?</h4>
            <p className="text-xs opacity-90 mt-1">List your property directly with SafeHouse. No brokerage.</p>
            <Button className="mt-3 w-full bg-white text-[#9a1b2f] hover:bg-gray-50" size="sm" onClick={() => navigate('/#top')}>Post Property Free</Button>
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <Skeleton className="h-32 w-full" />
                  <div className="p-4 space-y-2"><Skeleton className="h-4 w-2/3" /><Skeleton className="h-3 w-1/2" /></div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
              <User className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="font-bold text-gray-900">No owner listings match your filters</h3>
              <p className="text-sm text-gray-500 mt-1">Try adjusting the filters or check back soon.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {items.map((l) => <OwnerListingCard key={l.id} listing={l} />)}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OwnerListingsPage;
